import { useCallback, useEffect } from 'react';

import { useIsFetching, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import type { ResponseTypeWithStatus } from 'services/HttpClient';
import ParseEventStreamService from 'services/streamParser/streamParserService';
import type { StreamEvent } from 'services/streamParser/types';

type FetcherFn<Payload> = (payload: Payload, signal?: AbortSignal) => Promise<ResponseTypeWithStatus<Response>>;

type UseSSEStreamOptions<Payload = unknown> = {
  enabled?: boolean;
  payload?: Payload;
  queryKey: readonly unknown[];
  fetcher: FetcherFn<Payload>;
  onEvent: (event: StreamEvent) => void;
  onComplete?: VoidFunction;
  queryOptions?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>;
};

export const useSSEStream = <Payload>({
  enabled = true,
  payload,
  fetcher,
  onEvent,
  queryKey,
  onComplete,
  queryOptions,
}: UseSSEStreamOptions<Payload>) => {
  const queryClient = useQueryClient();

  const { isLoading, isPending, isFetching, isFetched, isError, isFetchedAfterMount, refetch } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!payload) {
        return { success: true };
      }

      const result = await fetcher(payload, signal);
      const response = result?.data;

      if (!response) {
        throw new Error('Request failed: no response received');
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }

      await ParseEventStreamService.parseEventStream(response, { onEvent });

      return { success: true };
    },
    enabled,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...queryOptions,
  });

  const fetchingCount = useIsFetching({ queryKey, exact: true });
  const isFetchingQuery = fetchingCount > 0;

  useEffect(() => {
    if (!isFetchingQuery && isFetchedAfterMount) {
      onComplete?.();
    }
  }, [isFetchingQuery, isFetchedAfterMount, onComplete]);

  const stop = useCallback(() => {
    return queryClient.cancelQueries({ queryKey });
  }, [queryClient, queryKey]);

  return { isLoading, isPending, isFetching, isFetched, isError, stop, isFetchedAfterMount, refetch };
};
